import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { FontIcon } from '@fluentui/react';
import SConsciousApiService from '../services/SConsciousApiService';

const _api = new SConsciousApiService();

interface ProfileData {
    user_id: string;
    user_name: string;
    photo_url: string | null;
    receipts_count: number;
    items_count: number;
    scons_points: number;
    friends_count: number;
    common_friends: number;
    friend_status: 'pending' | 'requested' | 'registered' | null;
    friend_invite_id: string | null;
    is_blocked: boolean;
}

export default function Profile() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const targetUserId = searchParams.get('userId') || '';
    const userId = localStorage.getItem('signed_in_id') || '';

    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);

    const fetchProfile = useCallback(async () => {
        if (!userId || !targetUserId) return;
        setLoading(true);
        const data = await _api.getUserProfile(userId, targetUserId);
        setProfile(data);
        setLoading(false);
    }, [userId, targetUserId]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    const handleAddFriend = async () => {
        setActionLoading(true);
        const result = await _api.sendFriendRequest(userId, targetUserId);
        if (result.success) {
            setProfile(prev => prev ? { ...prev, friend_status: 'pending' } : prev);
        }
        setActionLoading(false);
    };

    const handleRemoveFriend = async () => {
        if (!profile?.friend_invite_id) return;
        setActionLoading(true);
        await _api.removeFriend(userId, profile.friend_invite_id);
        setProfile(prev => prev ? { ...prev, friend_status: null, friend_invite_id: null } : prev);
        setActionLoading(false);
    };

    const handleAcceptRequest = async () => {
        if (!profile?.friend_invite_id) return;
        setActionLoading(true);
        await _api.acceptFriendRequest(userId, profile.friend_invite_id);
        setProfile(prev => prev ? { ...prev, friend_status: 'registered' } : prev);
        setActionLoading(false);
    };

    const handleCancelRequest = async () => {
        if (!profile?.friend_invite_id) return;
        setActionLoading(true);
        await _api.cancelInvite(userId, profile.friend_invite_id);
        setProfile(prev => prev ? { ...prev, friend_status: null, friend_invite_id: null } : prev);
        setActionLoading(false);
    };

    const handleBlock = async () => {
        setActionLoading(true);
        await _api.blockUser(userId, targetUserId);
        setProfile(prev => prev ? { ...prev, is_blocked: true } : prev);
        setActionLoading(false);
    };

    const handleUnblock = async () => {
        setActionLoading(true);
        await _api.unblockUser(userId, targetUserId);
        setProfile(prev => prev ? { ...prev, is_blocked: false } : prev);
        setActionLoading(false);
    };

    const handleMessage = () => {
        if (!profile) return;
        const convId = [userId, targetUserId].sort().join('_');
        const conv = {
            conversationId: convId,
            otherUserId: targetUserId,
            otherUserName: profile.user_name || '',
            type: 'chat',
            lastMessage: '',
            lastAt: '',
        };
        navigate('/Conversation', { state: { conv, backTo: `/Profile?userId=${targetUserId}` } });
    };

    if (loading) {
        return (
            <div style={{ background: '#ffcc00', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontIcon iconName="Sync" style={{ fontSize: 28, color: 'rgba(0,0,0,0.3)' }} />
            </div>
        );
    }

    if (!profile) {
        return (
            <div style={{ background: '#ffcc00', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <FontIcon iconName="UserOptional" style={{ fontSize: 48, color: 'rgba(0,0,0,0.2)' }} />
                <span style={{ color: 'rgba(0,0,0,0.4)', fontSize: 14 }}>User not found</span>
                <button className="actionButton" onClick={() => navigate(-1)} style={{ marginTop: 12, fontFamily: 'inherit' }}>Go back</button>
            </div>
        );
    }

    const isFriend = profile.friend_status === 'registered';
    const isPending = profile.friend_status === 'pending';
    const isRequested = profile.friend_status === 'requested';

    return (
        <div style={{ background: '#ffcc00', minHeight: '100vh', fontFamily: 'inherit' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '16px 16px 0', gap: 12 }}>
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        background: '#000', border: 'none', borderRadius: '50%',
                        width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontFamily: 'inherit', fontSize: 16, flexShrink: 0, color: '#ffcc00',
                    }}
                >
                    ←
                </button>
                <span style={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Profile</span>
            </div>

            {/* Profile card */}
            <div style={{ textAlign: 'center', padding: '24px 16px 8px' }}>
                {/* Avatar */}
                <div
                    onClick={() => { if (profile.photo_url) setShowPhotoLightbox(true); }}
                    style={{
                        width: 80, height: 80, borderRadius: '50%',
                        background: '#222', border: '3px solid rgba(0,0,0,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', margin: '0 auto 12px',
                        cursor: profile.photo_url ? 'pointer' : 'default',
                    }}
                >
                    {profile.photo_url ? (
                        <img src={profile.photo_url} alt={profile.user_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <span style={{ fontSize: 32, color: '#ffcc00', fontWeight: 700, lineHeight: 1 }}>
                            {(profile.user_name || '?')[0].toUpperCase()}
                        </span>
                    )}
                </div>

                {/* Photo lightbox */}
                {showPhotoLightbox && profile.photo_url && (
                    <div className="fidelity-lightbox" onClick={() => setShowPhotoLightbox(false)}>
                        <div className="fidelity-lightbox__inner" onClick={e => e.stopPropagation()}>
                            <div className="fidelity-lightbox__actions">
                                <button className="closeBtn" onClick={() => setShowPhotoLightbox(false)}>✕</button>
                            </div>
                            <img src={profile.photo_url} alt={profile.user_name} className="fidelity-lightbox__img" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
                        </div>
                    </div>
                )}

                {/* Name */}
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>{profile.user_name}</div>

                {/* Friendship badge */}
                {isFriend && (
                    <span style={{
                        background: '#000', color: '#ffcc00', borderRadius: 20, padding: '3px 12px',
                        fontSize: 11, fontWeight: 700, display: 'inline-block', marginBottom: 8,
                    }}>
                        Friends
                    </span>
                )}
                {isPending && (
                    <span style={{
                        background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.5)', borderRadius: 20, padding: '3px 12px',
                        fontSize: 11, fontWeight: 600, display: 'inline-block', marginBottom: 8,
                    }}>
                        Request sent
                    </span>
                )}
                {isRequested && (
                    <span style={{
                        background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.5)', borderRadius: 20, padding: '3px 12px',
                        fontSize: 11, fontWeight: 600, display: 'inline-block', marginBottom: 8,
                    }}>
                        Wants to be your friend
                    </span>
                )}
                {profile.is_blocked && (
                    <span style={{
                        background: 'rgba(180,0,0,0.15)', color: '#8b0000', borderRadius: 20, padding: '3px 12px',
                        fontSize: 11, fontWeight: 600, display: 'inline-block', marginBottom: 8,
                    }}>
                        Blocked
                    </span>
                )}
            </div>

            {/* Stats grid */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8,
                padding: '8px 16px 16px', maxWidth: 360, margin: '0 auto',
            }}>
                {[
                    { label: 'Receipts', value: profile.receipts_count, icon: 'Dataflows' },
                    { label: 'Products', value: profile.items_count, icon: 'ProductList' },
                    { label: "sCoN's", value: profile.scons_points, icon: 'Trophy' },
                    { label: 'Friends', value: profile.friends_count, icon: 'People' },
                ].map(stat => (
                    <div key={stat.label} style={{
                        background: 'rgba(0,0,0,0.08)', borderRadius: 16, padding: '14px 12px',
                        textAlign: 'center',
                    }}>
                        <FontIcon iconName={stat.icon} style={{ fontSize: 18, color: 'rgba(0,0,0,0.35)', display: 'block', marginBottom: 4 }} />
                        <div style={{ fontWeight: 800, fontSize: 22, lineHeight: 1.2 }}>{stat.value}</div>
                        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', fontWeight: 600, marginTop: 2 }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Common friends */}
            {profile.common_friends > 0 && (
                <div style={{ textAlign: 'center', padding: '0 16px 16px' }}>
                    <span style={{
                        background: 'rgba(0,0,0,0.06)', borderRadius: 20, padding: '6px 16px',
                        fontSize: 12, color: 'rgba(0,0,0,0.5)', display: 'inline-block',
                    }}>
                        <FontIcon iconName="People" style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
                        {profile.common_friends} friend{profile.common_friends !== 1 ? 's' : ''} in common
                    </span>
                </div>
            )}

            {/* Actions */}
            <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360, margin: '0 auto' }}>
                {/* Friend actions */}
                {!isFriend && !isPending && !isRequested && !profile.is_blocked && (
                    <button
                        className="actionButton"
                        disabled={actionLoading}
                        onClick={handleAddFriend}
                        style={{ fontFamily: 'inherit', width: '100%', opacity: actionLoading ? 0.5 : 1 }}
                    >
                        <FontIcon iconName="AddFriend" style={{ marginRight: 6, fontSize: 14, verticalAlign: 'middle' }} />
                        Add Friend
                    </button>
                )}
                {isRequested && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            className="actionButton"
                            disabled={actionLoading}
                            onClick={handleAcceptRequest}
                            style={{ fontFamily: 'inherit', flex: 2, opacity: actionLoading ? 0.5 : 1 }}
                        >
                            Accept
                        </button>
                        <button
                            className="actionButton"
                            disabled={actionLoading}
                            onClick={handleCancelRequest}
                            style={{
                                fontFamily: 'inherit', flex: 1, opacity: actionLoading ? 0.5 : 1,
                                background: 'rgba(0,0,0,0.08)', color: '#000',
                            }}
                        >
                            Decline
                        </button>
                    </div>
                )}
                {isPending && (
                    <button
                        className="actionButton"
                        disabled={actionLoading}
                        onClick={handleCancelRequest}
                        style={{
                            fontFamily: 'inherit', width: '100%', opacity: actionLoading ? 0.5 : 1,
                            background: 'rgba(0,0,0,0.08)', color: '#000',
                        }}
                    >
                        Cancel Request
                    </button>
                )}
                {isFriend && (
                    <>
                        <button
                            className="actionButton"
                            onClick={handleMessage}
                            style={{ fontFamily: 'inherit', width: '100%' }}
                        >
                            <FontIcon iconName="Chat" style={{ marginRight: 6, fontSize: 14, verticalAlign: 'middle' }} />
                            Message
                        </button>
                        <button
                            className="actionButton"
                            disabled={actionLoading}
                            onClick={handleRemoveFriend}
                            style={{
                                fontFamily: 'inherit', width: '100%', opacity: actionLoading ? 0.5 : 1,
                                background: 'rgba(0,0,0,0.08)', color: '#000',
                            }}
                        >
                            Remove Friend
                        </button>
                    </>
                )}

                {/* Block / Unblock */}
                {!profile.is_blocked ? (
                    <button
                        disabled={actionLoading}
                        onClick={handleBlock}
                        style={{
                            background: 'rgba(180,0,0,0.1)', color: '#8b0000',
                            border: 'none', borderRadius: 20, padding: '10px 20px',
                            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                            width: '100%', marginTop: 8, opacity: actionLoading ? 0.5 : 1,
                        }}
                    >
                        Block
                    </button>
                ) : (
                    <button
                        disabled={actionLoading}
                        onClick={handleUnblock}
                        style={{
                            background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.5)',
                            border: 'none', borderRadius: 20, padding: '10px 20px',
                            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                            width: '100%', marginTop: 8, opacity: actionLoading ? 0.5 : 1,
                        }}
                    >
                        Unblock
                    </button>
                )}
            </div>
        </div>
    );
}
